import { TheWestSection } from '@news-mono/common'
import { metrics } from '@news-mono/component-library'
import {
    ElectionDefinition,
    GetRouteAdTargeting,
    getSocialImageMeta,
    PageType,
    getElectionAdTargeting,
    TogglesReduxState,
    isElectionFeatureEnabled,
} from '@news-mono/web-common'
import { layout } from '../../../App.routing'
import { getYear } from 'date-fns'
import { wrapWithElectionContext } from '../routes'

type CreateElectionPageArgs = {
    getAdTargeting: GetRouteAdTargeting
    electionDefinition: ElectionDefinition
    toggles: TogglesReduxState
}

export const createElectionElectoratePage = ({
    getAdTargeting,
    electionDefinition,
    toggles,
}: CreateElectionPageArgs): PageType<TheWestSection> => {
    const isHeadToHeadEnabled = isElectionFeatureEnabled(
        toggles,
        electionDefinition.electionId,
        'election-section-head-to-head',
    )

    const isHeadToHeadCountdownEnabled = isElectionFeatureEnabled(
        toggles,
        electionDefinition.electionId,
        'election-head-to-head-countdown',
    )

    const isPrefixEnabled = isElectionFeatureEnabled(
        toggles,
        electionDefinition.electionId, 
        `election-live-title-prefix`
    )
    const customHeading = isPrefixEnabled ? 'Live Electorate Results' : 'Electorate Results'

    const isFederal =
        electionDefinition.electionData?.config?.electionType.toLowerCase() ===
        'federal'

    const electionYear = getYear(
        new Date(electionDefinition.electionData?.config?.electionDate ?? ''),
    )
    const electionState = electionDefinition.electionData?.config?.state
    const meta = electionDefinition.meta

    let defaultTitle
    let collectionHeading

    if (isFederal) {
        defaultTitle = `Federal election ${electionYear} electorate results`
        collectionHeading = `Latest Federal Election Updates`
    } else {
        defaultTitle = `${electionState} State election ${electionYear} electorate results`
        collectionHeading = `Latest ${electionState} Election Updates`
    }

    const seoTitle = meta.electorates?.seoTitle ?? defaultTitle
    const seoDescription = meta.electorates?.seoDescription

    return {
        kind: 'page',
        heading: seoTitle,
        pageType: 'static',
        section: 'default',
        hideHeading: true,
        adTargeting: getElectionAdTargeting(
            electionDefinition,
            getAdTargeting,
            'electorates-page',
        ),
        pageMeta: {
            title: seoTitle,
            description: seoDescription,
            link: meta.canonical
                ? [
                      {
                          rel: 'canonical',
                          href: `${meta.canonical}politics/${electionDefinition.electionId}/electorates`,
                      },
                  ]
                : undefined,
            meta: meta.electorates?.socialImage
                ? getSocialImageMeta(
                      meta.electorates.socialImage,
                      '2400',
                      '1260',
                  )
                : undefined,
        },
        socialMeta: {
            title: seoTitle,
            description: seoDescription,
        },
        compositions: wrapWithElectionContext([
            layout.nestedComposition({
                type: 'box',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                verticalSpacing: 'unset',
                                breakpoints: {
                                    lg: {
                                        verticalSpacing: 'unset',
                                        horizontalGuttersOverride: 15,
                                    },
                                },
                            },
                            contentAreas: {
                                main: [
                                    ...(isHeadToHeadEnabled
                                        ? [
                                              layout.component({
                                                  type: 'election-head-to-head-seat-count-widget',
                                                  props: {
                                                      electionDefinition,
                                                      countdownEnabled:
                                                          isHeadToHeadCountdownEnabled,
                                                  },
                                              }),
                                          ]
                                        : []),
                                    layout.component({
                                        type: 'election-navigation',
                                        props: {
                                            electionDefinition,
                                            activePage: 'electorate-results',
                                            headingLevel: 'h2',
                                        },
                                    }),
                                ],
                            },
                        }),

                        layout.nestedComposition({
                            type: 'athena',
                            props: {
                                containerWidth:
                                    metrics.thewest.siteMetrics
                                        .mainContentWidth,
                                elementType: 'div',
                                sidebarOptions: 'hiddenMobile',
                                hasHorizontalGutters: false,
                            },
                            contentAreas: {
                                sidebar: [
                                    layout.component({
                                        type: 'ad-unit',
                                        props: {
                                            noticePosition: 'above-center',
                                            padding: [
                                                0,
                                                0,
                                                metrics.thewest.margins.md,
                                                0,
                                            ],
                                            slot: {
                                                id: 'sidebar-top',
                                                size: 'desktopMrecHalfPage',
                                                pageType: 'static',
                                            },
                                            adType: 'inline',
                                        },
                                    }),
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            stickyOffset: 155,
                                            fillContainer: true,
                                        },
                                        contentAreas: {
                                            main: [
                                                layout.component({
                                                    type: 'juliet',
                                                    props: {
                                                        cardType: {
                                                            type: 'landscape',
                                                            format: 'landscape-smallImage',
                                                        },
                                                        removeHorizontalGutters:
                                                            true,
                                                        kickerMode: 'hidden',
                                                        contentIsTangential:
                                                            true,
                                                        sectionHeader: {
                                                            heading:
                                                                collectionHeading,
                                                        },
                                                        dataDefinitionArgs: {
                                                            type: 'curation',
                                                            name: electionDefinition.electionId,
                                                            pageSize: 4,
                                                            offset: 0,
                                                        },
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],

                                main: [
                                    layout.component({
                                        type: 'election-full-electorate-widget',
                                        props: {
                                            electionDefinition,
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ]),
    }
}
